import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  MenuItem,
  Divider,
  Paper,
} from '@mui/material';
import { ArrowBack, CloudUpload, CheckCircle, Cancel, Save } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { campaignService, campaignCategoryService, extractApiDataList } from '../services/api';
import { getAssetUrl } from '../config';

const CampaignSubmissionReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [submitter, setSubmitter] = useState({ name: '', email: '', phone: '' });
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    content: '',
    target_amount: '',
    start_date: '',
    end_date: '',
    category_id: '',
    status: 'pending',
    admin_notes: '',
    image_url: '',
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [campaignRes, categoriesRes] = await Promise.all([
        campaignService.getCampaignById(id),
        campaignCategoryService.getAll({ include_inactive: 1 }),
      ]);
      const campaign = campaignRes.data?.data || campaignRes.data;
      setCategories(extractApiDataList(categoriesRes));
      setSubmitter({
        name: campaign.submitter_name || '',
        email: campaign.submitter_email || '',
        phone: campaign.submitter_phone || '',
      });
      setFormData({
        title: campaign.title || '',
        slug: campaign.slug || '',
        description: campaign.description || '',
        content: campaign.content || '',
        target_amount: campaign.target_amount ?? '',
        start_date: campaign.start_date ? String(campaign.start_date).slice(0, 10) : '',
        end_date: campaign.end_date ? String(campaign.end_date).slice(0, 10) : '',
        category_id: campaign.category_id ? String(campaign.category_id) : '',
        status: campaign.status || 'pending',
        admin_notes: campaign.admin_notes || '',
        image_url: campaign.image_url || '',
      });
      if (campaign.image_url) {
        setImagePreview(getAssetUrl(campaign.image_url));
      }
    } catch (err) {
      setToast({ open: true, message: 'Failed to load campaign', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const buildPayload = (statusOverride) => {
    const fd = new FormData();
    const payload = { ...formData, status: statusOverride || formData.status };
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) fd.append(key, value);
    });
    if (selectedImage) fd.append('image', selectedImage);
    return fd;
  };

  const saveCampaign = async (statusOverride) => {
    if (!formData.title || !formData.description) {
      setToast({ open: true, message: 'Title and description are required', type: 'error' });
      return false;
    }
    setSaving(true);
    try {
      if (selectedImage) {
        await campaignService.updateCampaignWithFile(id, buildPayload(statusOverride));
      } else {
        const body = { ...formData, status: statusOverride || formData.status };
        await campaignService.updateCampaign(id, body);
      }
      return true;
    } catch (err) {
      setToast({ open: true, message: err.response?.data?.error || 'Save failed', type: 'error' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const ok = await saveCampaign();
    if (ok) {
      setToast({ open: true, message: 'Changes saved', type: 'success' });
      await loadData();
    }
  };

  const handleApprove = async () => {
    const ok = await saveCampaign('active');
    if (!ok) return;
    await campaignService.updateCampaignStatus(id, { status: 'active', admin_notes: formData.admin_notes });
    setToast({ open: true, message: 'Campaign approved and published', type: 'success' });
    setTimeout(() => navigate('/admin/campaign-submissions'), 1200);
  };

  const handleReject = async () => {
    const ok = await saveCampaign('rejected');
    if (!ok) return;
    await campaignService.updateCampaignStatus(id, { status: 'rejected', admin_notes: formData.admin_notes });
    setToast({ open: true, message: 'Campaign rejected', type: 'success' });
    setTimeout(() => navigate('/admin/campaign-submissions'), 1200);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
      <Container maxWidth="md">
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/campaign-submissions')} sx={{ mb: 2 }}>
          Back to submissions
        </Button>

        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
          Review campaign submission
        </Typography>

        <Paper sx={{ p: 2, mb: 3, bgcolor: '#fff' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Submitted by
          </Typography>
          <Typography>{submitter.name}</Typography>
          <Typography variant="body2">{submitter.email}</Typography>
          <Typography variant="body2">{submitter.phone}</Typography>
        </Paper>

        <Card sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth label="Category" name="category_id" value={formData.category_id} onChange={handleChange}>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth required label="Title" name="title" value={formData.title} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Slug" name="slug" value={formData.slug} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required multiline rows={3} label="Description" name="description" value={formData.description} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={4} label="Content" name="content" value={formData.content} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Goal amount" name="target_amount" type="number" value={formData.target_amount} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Start date" name="start_date" type="date" value={formData.start_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="End date" name="end_date" type="date" value={formData.end_date} onChange={handleChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
                Change image
                <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
              </Button>
              {imagePreview && (
                <Box component="img" src={imagePreview} alt="" sx={{ display: 'block', mt: 2, maxWidth: 320, borderRadius: 2 }} />
              )}
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Admin notes (internal / rejection reason)"
                name="admin_notes"
                value={formData.admin_notes}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 4 }}>
            <Button variant="outlined" startIcon={<Save />} onClick={handleSave} disabled={saving}>
              Save changes
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={handleApprove}
              disabled={saving}
              sx={{ bgcolor: '#2c5f2d' }}
            >
              Approve & publish
            </Button>
            <Button variant="contained" color="error" startIcon={<Cancel />} onClick={handleReject} disabled={saving}>
              Reject
            </Button>
          </Box>
        </Card>
      </Container>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.type}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignSubmissionReviewPage;
